import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLICY_CATEGORIES = [
  'Leave Policy', 'Code of Conduct', 'Benefits', 'Attendance Policy',
  'Remote Work Policy', 'Expense Policy', 'Security Policy',
  'Anti-Harassment Policy', 'General'
];

function HRPolicies({ user, onLogout }) {
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPolicyView, setShowPolicyView] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [acknowledgedPolicies, setAcknowledgedPolicies] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'General',
    document_url: '',
    version: '1.0',
    effective_date: new Date().toISOString().split('T')[0],
    acknowledgment_required: true,
    is_active: true
  });

  useEffect(() => {
    fetchPolicies();
    fetchDashboardStats();
    fetchAcknowledgedPolicies();
  }, [filterCategory]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchPolicies = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = filterCategory ? `?category=${filterCategory}` : '';
      const response = await axios.get(`${API}/policies${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPolicies(response.data);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
    }
  };

  const fetchAcknowledgedPolicies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/my-policies/acknowledged`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAcknowledgedPolicies(response.data);
    } catch (error) {
      console.error('Failed to fetch acknowledged policies:', error);
    }
  };

  const handleViewPolicy = async (policy) => {
    setSelectedPolicy(policy);
    setShowPolicyView(true);
  };

  const handleAcknowledge = async (policyId) => {
    if (!window.confirm('By clicking OK, you acknowledge that you have read and understood this policy.')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/policies/${policyId}/acknowledge`, {
        ip_address: 'Web Client',
        comments: 'Acknowledged via web portal'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Policy acknowledged successfully!');
      fetchAcknowledgedPolicies();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to acknowledge policy: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (selectedPolicy && !showPolicyView) {
        await axios.put(`${API}/policies/${selectedPolicy.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Policy updated successfully!');
      } else {
        await axios.post(`${API}/policies`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Policy created successfully!');
      }
      resetForm();
      fetchPolicies();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to save policy: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (policy) => {
    setSelectedPolicy(policy);
    setFormData({ ...policy });
    setShowForm(true);
    setShowPolicyView(false);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'General',
      document_url: '',
      version: '1.0',
      effective_date: new Date().toISOString().split('T')[0],
      acknowledgment_required: true,
      is_active: true
    });
    setSelectedPolicy(null);
    setShowForm(false);
    setShowPolicyView(false);
  };

  const openAddForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'General',
      document_url: '',
      version: '1.0',
      effective_date: new Date().toISOString().split('T')[0],
      acknowledgment_required: true,
      is_active: true
    });
    setSelectedPolicy(null);
    setShowPolicyView(false);
    setShowForm(true);
  };

  const isAcknowledged = (policyId) => {
    return acknowledgedPolicies.includes(policyId);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>HR Policies</h1>
          {(user.role === 'Admin' || user.role === 'HR Manager') && (
            <button
              onClick={openAddForm}
              className="px-6 py-3 rounded-lg text-white font-medium"
              style={{backgroundColor: '#2b3380'}}
            >
              + Add Policy
            </button>
          )}
        </div>

        {/* HR Policies Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="HR Policies Overview"
            icon="📋"
            bgColor="#7e57c2"
            stats={[
              {
                label: 'Total Policies',
                value: dashboardStats.total_policies || 0,
                subtitle: 'Active policies',
                color: '#7e57c2'
              },
              {
                label: 'Pending Acknowledgments',
                value: dashboardStats.pending_acknowledgments || 0,
                subtitle: 'Require your action',
                color: '#ff9800'
              },
              {
                label: 'Acknowledged',
                value: acknowledgedPolicies.length || 0,
                subtitle: 'By you',
                color: '#4caf50'
              },
              {
                label: 'Policy Categories',
                value: '9',
                subtitle: 'Different types',
                color: '#607d8b'
              }
            ]}
          />
        )}

        {/* Policy View Modal */}
        {showPolicyView && selectedPolicy && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {selectedPolicy.title}
                  </h2>
                  <button onClick={resetForm} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex gap-4 text-sm">
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full">
                      {selectedPolicy.category}
                    </span>
                    <span className="px-3 py-1 bg-[#2b338030] text-[#2b3380] rounded-full">
                      Version {selectedPolicy.version}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full">
                      Effective: {formatDate(selectedPolicy.effective_date)}
                    </span>
                  </div>

                  {selectedPolicy.description && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700">{selectedPolicy.description}</p>
                    </div>
                  )}

                  {selectedPolicy.document_url && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <div className="text-5xl mb-3">📄</div>
                      <p className="text-gray-700 mb-4">Policy Document</p>
                      <a
                        href={selectedPolicy.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-6 py-2 bg-[#2b3380] text-white rounded-lg inline-block hover:bg-[#1f2660]"
                      >
                        📥 View/Download Document
                      </a>
                    </div>
                  )}

                  {selectedPolicy.acknowledgment_required && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-800 font-medium mb-2">
                        ⚠️ Acknowledgment Required
                      </p>
                      <p className="text-sm text-yellow-700">
                        You must acknowledge that you have read and understood this policy.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 justify-end">
                  {(user.role === 'Admin' || user.role === 'HR Manager') && (
                    <button
                      onClick={() => handleEdit(selectedPolicy)}
                      className="px-6 py-2 rounded-lg border"
                    >
                      Edit Policy
                    </button>
                  )}
                  {selectedPolicy.acknowledgment_required && !isAcknowledged(selectedPolicy.id) && (
                    <button
                      onClick={() => handleAcknowledge(selectedPolicy.id)}
                      className="px-6 py-2 rounded-lg text-white"
                      style={{backgroundColor: '#4caf50'}}
                    >
                      ✓ I Acknowledge
                    </button>
                  )}
                  {isAcknowledged(selectedPolicy.id) && (
                    <div className="px-6 py-2 rounded-lg bg-green-100 text-green-800 font-medium">
                      ✓ Acknowledged
                    </div>
                  )}
                  <button
                    onClick={resetForm}
                    className="px-6 py-2 rounded-lg"
                    style={{backgroundColor: '#2b3380', color: 'white'}}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Policy Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {selectedPolicy ? 'Edit Policy' : 'Add New Policy'}
                  </h2>
                  <button onClick={resetForm} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Policy Title *</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Category *</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      >
                        {POLICY_CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        rows="3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Document URL</label>
                      <input
                        type="text"
                        value={formData.document_url}
                        onChange={(e) => setFormData({...formData, document_url: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="https://example.com/policy.pdf"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Version</label>
                        <input
                          type="text"
                          value={formData.version}
                          onChange={(e) => setFormData({...formData, version: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Effective Date *</label>
                        <input
                          type="date"
                          value={formData.effective_date}
                          onChange={(e) => setFormData({...formData, effective_date: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          required
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.acknowledgment_required}
                          onChange={(e) => setFormData({...formData, acknowledgment_required: e.target.checked})}
                          className="mr-2"
                        />
                        <span className="text-sm">Acknowledgment Required</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                          className="mr-2"
                        />
                        <span className="text-sm">Active</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border">
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-lg text-white"
                      style={{backgroundColor: '#2b3380'}}
                    >
                      {selectedPolicy ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <label className="block text-sm font-medium mb-2">Filter by Category</label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full md:w-1/3 px-3 py-2 border rounded-lg"
          >
            <option value="">All Categories</option>
            {POLICY_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Policies List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {policies.map((policy) => (
            <div key={policy.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-bold flex-1">{policy.title}</h3>
                {isAcknowledged(policy.id) && (
                  <span className="text-green-600 text-2xl" title="Acknowledged">✓</span>
                )}
              </div>
              <div className="mb-3">
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                  {policy.category}
                </span>
              </div>
              {policy.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{policy.description}</p>
              )}
              <div className="text-xs text-gray-500 mb-4">
                <p>Version: {policy.version}</p>
                <p>Effective: {formatDate(policy.effective_date)}</p>
              </div>
              <button
                onClick={() => handleViewPolicy(policy)}
                className="w-full py-2 rounded-lg text-white"
                style={{backgroundColor: '#7e57c2'}}
              >
                View Policy
              </button>
            </div>
          ))}
        </div>

        {policies.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📋</div>
            <p className="text-gray-600">No policies found</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default HRPolicies;